import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ChevronDown,
  ChevronUp,
  GripVertical,
  Plus,
  Trash2,
} from 'lucide-react';
import React, { useState } from 'react';

import type { SectionType } from '../../../types/blog';
import { ChecklistSectionEditor } from './sectionEditors/ChecklistSectionEditor';
import { ComparisonSectionEditor } from './sectionEditors/ComparisonSectionEditor';
import { CtaSectionEditor } from './sectionEditors/CtaSectionEditor';
import { ImageSectionEditor } from './sectionEditors/ImageSectionEditor';
import { StepSectionEditor } from './sectionEditors/StepSectionEditor';
import { TextSectionEditor } from './sectionEditors/TextSectionEditor';
import { GallerySectionEditor } from './sectionEditors/GallerySectionEditor';
import { TocSectionEditor } from './sectionEditors/TocSectionEditor';

export interface EditorSection {
  _id: string; // client-side stable id for dnd
  type: SectionType;
  heading: string;
  data: Record<string, unknown>;
}

interface Props {
  sections: EditorSection[];
  onChange: (sections: EditorSection[]) => void;
}

const TYPE_LABELS: Record<SectionType, string> = {
  text: 'Text',
  step: 'Steps',
  checklist: 'Checklist',
  comparison: 'Comparison',
  cta: 'CTA',
  toc: 'Table of Contents',
  image: 'Image',
  gallery: 'Gallery',
};

const SECTION_DEFAULTS: Record<SectionType, Record<string, unknown>> = {
  text: { content_md: '' },
  step: { steps: [{ title: '', description: '' }] },
  checklist: { items: [''] },
  comparison: { before: '', after: '' },
  cta: { headline: '', description: '', button_text: '', button_link: '' },
  toc: { items: [], align: 'top' },
  image: { url: '', alt: '', caption: '' },
  gallery: { images: [{ url: '', alt: '', caption: '' }, { url: '', alt: '', caption: '' }], columns: 2 },
};

const ADD_TYPES: SectionType[] = ['text', 'step', 'checklist', 'comparison', 'cta', 'image', 'gallery', 'toc'];

// ── Individual sortable section card ─────────────────────────────────────────

interface SectionCardProps {
  section: EditorSection;
  /** Headings from all OTHER sections — forwarded to TocSectionEditor for auto-sync */
  siblingHeadings: string[];
  onUpdate: (patch: Partial<EditorSection>) => void;
  onRemove: () => void;
}

const SectionCard: React.FC<SectionCardProps> = ({ section, siblingHeadings, onUpdate, onRemove }) => {
  const [collapsed, setCollapsed] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: section._id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  const updateData = (data: Record<string, unknown>) => onUpdate({ data });

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="ds-card-sm overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b"
        style={{ background: 'var(--ds-bg-tinted)', borderColor: 'var(--ds-border)' }}>
        <button
          type="button"
          {...listeners}
          {...attributes}
          className="cursor-grab active:cursor-grabbing t-muted hover:text-[var(--ds-text-strong)] touch-none"
          title="Drag to reorder"
        >
          <GripVertical className="w-4 h-4" />
        </button>

        <span className="t-label flex-1">
          {TYPE_LABELS[section.type]}
        </span>

        <input
          value={section.heading}
          onChange={(e) => onUpdate({ heading: e.target.value })}
          placeholder="Section heading (optional)"
          className="ds-input ds-input-sm w-48"
          onClick={(e) => e.stopPropagation()}
        />

        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="ds-btn ds-btn-ghost ds-btn-sm p-0 w-7"
        >
          {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </button>

        <button
          type="button"
          onClick={onRemove}
          className="ds-btn ds-btn-ghost ds-btn-sm p-0 w-7 hover:text-[var(--ds-danger)]"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Body */}
      {!collapsed && (
        <div className="p-4">
          {section.type === 'text' && (
            <TextSectionEditor
              data={section.data as { content_md: string }}
              onChange={updateData}
            />
          )}
          {section.type === 'step' && (
            <StepSectionEditor
              data={section.data as { steps: { title: string; description?: string }[] }}
              onChange={updateData}
            />
          )}
          {section.type === 'checklist' && (
            <ChecklistSectionEditor
              data={section.data as { items: string[] }}
              onChange={updateData}
            />
          )}
          {section.type === 'comparison' && (
            <ComparisonSectionEditor
              data={section.data as { before: string; after: string }}
              onChange={updateData}
            />
          )}
          {section.type === 'cta' && (
            <CtaSectionEditor
              data={
                section.data as {
                  headline: string;
                  description?: string;
                  button_text?: string;
                  button_link?: string;
                }
              }
              onChange={updateData}
            />
          )}
          {section.type === 'image' && (
            <ImageSectionEditor
              data={section.data as { url: string; alt?: string; caption?: string }}
              onChange={updateData}
            />
          )}
          {section.type === 'gallery' && (
            <GallerySectionEditor
              data={section.data as { images: { url: string; alt?: string; caption?: string }[]; columns?: 1 | 2 | 3 }}
              onChange={updateData}
            />
          )}
          {section.type === 'toc' && (
            <TocSectionEditor
              data={section.data as { items: string[]; align?: 'top' | 'left' | 'right' }}
              onChange={updateData}
              sectionHeadings={siblingHeadings}
            />
          )}
        </div>
      )}
    </div>
  );
};

// ── Main list ────────────────────────────────────────────────────────────────

export const SectionsList: React.FC<Props> = ({ sections, onChange }) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = sections.findIndex((s) => s._id === active.id);
      const newIndex = sections.findIndex((s) => s._id === over.id);
      onChange(arrayMove(sections, oldIndex, newIndex));
    }
  };

  const addSection = (type: SectionType) => {
    onChange([
      ...sections,
      {
        _id: crypto.randomUUID(),
        type,
        heading: '',
        data: { ...SECTION_DEFAULTS[type] },
      },
    ]);
  };

  const updateSection = (idx: number, patch: Partial<EditorSection>) => {
    onChange(sections.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const removeSection = (idx: number) =>
    onChange(sections.filter((_, i) => i !== idx));

  return (
    <div className="flex flex-col gap-4">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={sections.map((s) => s._id)}
          strategy={verticalListSortingStrategy}
        >
          {sections.map((section, idx) => (
            <SectionCard
              key={section._id}
              section={section}
              siblingHeadings={sections
                .filter((_, i) => i !== idx)
                .map((s) => s.heading.trim())
                .filter(Boolean)}
              onUpdate={(patch) => updateSection(idx, patch)}
              onRemove={() => removeSection(idx)}
            />
          ))}
        </SortableContext>
      </DndContext>

      {sections.length === 0 && (
        <div className="ds-card text-center py-8">
          <p className="t-h3">No sections yet</p>
          <p className="t-meta mt-1">Add your first section below.</p>
        </div>
      )}

      {/* Add section buttons */}
      <div className="flex flex-wrap gap-2">
        {ADD_TYPES.map((type) => (
          <button
            key={type}
            type="button"
            onClick={() => addSection(type)}
            className="ds-btn ds-btn-secondary ds-btn-sm"
          >
            <Plus className="w-3.5 h-3.5" />
            {TYPE_LABELS[type]}
          </button>
        ))}
      </div>
    </div>
  );
};
